import * as Utils from "@/utils.js";
import apiClient from "./Environment";
import {
  USE_MOCKS,
  withGuardrailConfig,
  withGuardrailRuleTypes,
  withStrategyGuardrails,
  mockGetGuardrails,
  mockGetGuardrailTemplates,
  mockSetGuardrail,
  mockDeleteGuardrail,
  mockDuplicateGuardrail,
  mockSetStrategyGuardrail,
  mockDeleteStrategyGuardrail,
  mockSortStrategyGuardrails
} from "./mocks/pricingStrategyMocks";

export default {
  // Strategies
  getStrategies(params) {
    return apiClient.get("/get-strategies" + Utils.buildGetParams(params));
  },
  getStrategy(params) {
    const request = apiClient.get(
      "/get-strategy" + Utils.buildGetParams(params)
    );
    return USE_MOCKS ? request.then(withStrategyGuardrails) : request;
  },
  setStrategy(params) {
    return apiClient.post("/set-strategy", params);
  },
  deleteStrategy(params) {
    return apiClient.post("/delete-strategy", params);
  },
  getStrategyRates(params) {
    return apiClient.get("/get-strategy-rates" + Utils.buildGetParams(params));
  },
  getAnticipationSlots(params) {
    return apiClient.get(
      "/get-anticipation-slots" + Utils.buildGetParams(params)
    );
  },
  getStrategyRoomTypes(params) {
    return apiClient.get(
      "/get-strategy-room-types" + Utils.buildGetParams(params)
    );
  },
  // Strategy Rules
  setStrategyRule(params) {
    return apiClient.post("/set-strategy-rule", params);
  },
  deleteStrategyRule(params) {
    return apiClient.post("/delete-strategy-rule", params);
  },
  deleteStrategyRow(params) {
    return apiClient.post("/delete-strategy-row", params);
  },
  // Strategy Guardrails
  // Endpoints propios: el payload no lleva row_id, anticipation_slot_id ni
  // length (ocupan toda la antelación), y sí position (orden de evaluación).
  // Los tres devuelven la estrategia completa. En modo mock se recupera la
  // real y solo se le sustituye strategyGuardrails.
  setStrategyGuardrail(params) {
    if (USE_MOCKS) {
      return this.getStrategy({ strategy_id: params.strategy_id }).then(r =>
        mockSetStrategyGuardrail(params, r.data)
      );
    }
    return apiClient.post("/set-strategy-guardrail", params);
  },
  deleteStrategyGuardrail(params) {
    if (USE_MOCKS) {
      return this.getStrategy({ strategy_id: params.strategy_id }).then(r =>
        mockDeleteStrategyGuardrail(params, r.data)
      );
    }
    return apiClient.post("/delete-strategy-guardrail", params);
  },
  sortStrategyGuardrails(params) {
    if (USE_MOCKS) {
      return this.getStrategy({ strategy_id: params.strategy_id }).then(r =>
        mockSortStrategyGuardrails(params, r.data)
      );
    }
    return apiClient.post("/sort-strategy-guardrails", params);
  },
  // Strategies Calendar
  getPlanning(params) {
    return apiClient.get(
      "/get-strategies-calendar" + Utils.buildGetParams(params)
    );
  },
  setPlanning(params) {
    return apiClient.post("/set-strategies-calendar", params);
  },
  // Suggestions
  getSuggestions(params) {
    return apiClient.get(
      "/get-pricing-suggestions" + Utils.buildGetParams(params)
    );
  },
  applySuggestions(params) {
    return apiClient.post("/accept-pricing-suggestions", params);
  },
  ignoreSuggestion(params) {
    return apiClient.post("/ignore-pricing-suggestions", params);
  },
  reactivateSuggestion(params) {
    return apiClient.post("/reactivate-pricing-suggestions", params);
  },
  // Development environments only: creates real suggestions in DB from the
  // configured rules, with made up measures (UI testing).
  generateFakeSuggestions(params) {
    return apiClient.post("/generate-fake-pricing-suggestions", params);
  },
  // Development environments only: evaluates an unsaved rule against the real
  // data of a date, using the real engine (veetal-services). Stores nothing.
  testStrategyRule(params) {
    return apiClient.post("/test-strategy-rule", params);
  },

  // Strategy config & rules
  getStrategyConfig(params) {
    const request = apiClient.get(
      "/get-strategy-config" + Utils.buildGetParams(params)
    );
    return USE_MOCKS ? request.then(withGuardrailConfig) : request;
  },
  getRuleTypes(params) {
    const request = apiClient.get(
      "/get-rule-types" + Utils.buildGetParams(params)
    );
    return USE_MOCKS ? request.then(withGuardrailRuleTypes) : request;
  },
  getRules(params) {
    return apiClient.get("/get-rules" + Utils.buildGetParams(params));
  },
  getRuleTemplates(params) {
    return apiClient.get("/get-rule-templates" + Utils.buildGetParams(params));
  },
  // Lectura separada de /get-rules y /get-rule-templates: así la respuesta de
  // esos dos no cambia y ningún consumidor actual de state.rules pasa a
  // depender de filtrar por rule_kind. La escritura sí es compartida
  // (setRule / deleteRule / duplicateRule, con rule_kind en el payload).
  getGuardrails(params) {
    if (USE_MOCKS) return mockGetGuardrails(params);
    return apiClient.get("/get-guardrails" + Utils.buildGetParams(params));
  },
  getGuardrailTemplates(params) {
    if (USE_MOCKS) return mockGetGuardrailTemplates(params);
    return apiClient.get(
      "/get-guardrail-templates" + Utils.buildGetParams(params)
    );
  },
  setRule(params) {
    return apiClient.post("/set-rule", params);
  },
  deleteRule(params) {
    return apiClient.post("/delete-rule", params);
  },
  duplicateRule(params) {
    return apiClient.post("/duplicate-rule", params);
  },
  // Escritura de guardarailes: endpoints propios, no compartidos con reglas
  setGuardrail(params) {
    if (USE_MOCKS) return mockSetGuardrail(params);
    return apiClient.post("/set-guardrail", params);
  },
  deleteGuardrail(params) {
    if (USE_MOCKS) return mockDeleteGuardrail(params);
    return apiClient.post("/delete-guardrail", params);
  },
  duplicateGuardrail(params) {
    if (USE_MOCKS) return mockDuplicateGuardrail(params);
    return apiClient.post("/duplicate-guardrail", params);
  }
};
